import type {
  ApiNodeDataCustomData,
  ChoiceButtons,
  CustomRequirementPromptConfigShortText,
  DataIdentifier,
  Iso3166TwoDigitCountryCode,
} from '@onefootprint/request-types/dashboard';
import type { ChoiceFormData, CustomQuestionsFormData } from '../types';

const toOptionalString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

// `[]` would be treated as "no country allowed" by the backend, so collapse empty to undefined.
const stripSupportedCountries = (v: unknown): Iso3166TwoDigitCountryCode[] | undefined =>
  Array.isArray(v) && v.length > 0 ? (v as Iso3166TwoDigitCountryCode[]) : undefined;

const convertChoice = ({ saveValue, dataIdentifier, buttons }: ChoiceFormData): ChoiceButtons | undefined => {
  if (buttons.length === 0) return undefined;
  return {
    di: saveValue ? (dataIdentifier as DataIdentifier) : undefined,
    buttons: buttons.map(({ label, value, style }) => ({ label, style, value: saveValue ? value : undefined })),
  };
};

const convertToApiPayload = (formData: CustomQuestionsFormData): ApiNodeDataCustomData['page'] => ({
  header: formData.title,
  subheader: formData.subtitle || undefined,
  choice: convertChoice(formData.choice),
  sections: formData.questions.map(question => {
    if (question.kind === 'text_block') {
      return {
        kind: 'text_block' as const,
        content: question.content,
        condition: question.condition,
      };
    }

    const basePrompt = {
      kind: 'prompt' as const,
      di: question.dataIdentifier as DataIdentifier,
      prompt: question.label,
      required: question.required,
      condition: question.condition,
    };

    if (question.kind === 'short_text') {
      const baseConfig = { kind: 'short_text' as const, format: question.format };
      if (question.format === 'any') {
        return {
          ...basePrompt,
          config: {
            ...baseConfig,
            placeholder: question.placeholder,
            regex: toOptionalString(question.regex),
            minLength: toOptionalString(question.minLength),
            maxLength: toOptionalString(question.maxLength),
            errorMessage: toOptionalString(question.errorMessage),
          } as CustomRequirementPromptConfigShortText,
        };
      }
      if (question.format === 'dob') {
        return {
          ...basePrompt,
          config: {
            ...baseConfig,
            placeholder: question.placeholder,
            minAge: toOptionalString(question.minAge),
            maxAge: toOptionalString(question.maxAge),
          } as CustomRequirementPromptConfigShortText,
        };
      }
      if (question.format === 'email') {
        return {
          ...basePrompt,
          config: {
            ...baseConfig,
            placeholder: question.placeholder,
            disablePlusAddressing: question.disablePlusAddressing ?? false,
          } as CustomRequirementPromptConfigShortText,
        };
      }
      if (question.format === 'country') {
        return {
          ...basePrompt,
          config: {
            ...baseConfig,
            placeholder: question.placeholder,
            supportedCountries: stripSupportedCountries(question.supportedCountries),
          } as CustomRequirementPromptConfigShortText,
        };
      }
      return {
        ...basePrompt,
        config: {
          ...baseConfig,
          placeholder: question.placeholder,
        } as CustomRequirementPromptConfigShortText,
      };
    }

    if (question.kind === 'long_text') {
      return {
        ...basePrompt,
        config: {
          kind: 'long_text' as const,
          placeholder: question.placeholder,
        },
      };
    }

    if (question.kind === 'number') {
      const isCurrency = question.numberFormat === 'currency';
      return {
        ...basePrompt,
        config: {
          kind: 'number' as const,
          min: question.min ? Number(question.min) : undefined,
          max: question.max ? Number(question.max) : undefined,
          format: isCurrency
            ? {
                formatKind: 'currency' as const,
                code: question.currencyCode ?? 'usd',
              }
            : {
                formatKind: 'any' as const,
                min: question.min ? Number(question.min) : undefined,
                max: question.max ? Number(question.max) : undefined,
              },
          placeholder: question.placeholder,
        },
      };
    }

    if (question.kind === 'checkbox') {
      return {
        ...basePrompt,
        config: {
          kind: 'checkbox' as const,
        },
      };
    }

    if (question.kind === 'dropdown') {
      return {
        ...basePrompt,
        config: {
          kind: 'dropdown' as const,
          options: question.options.map(o => o.label),
          includeOther: question.includeOther ?? false,
          placeholder: question.placeholder,
        },
      };
    }

    if (question.kind === 'checkboxes') {
      return {
        ...basePrompt,
        config: {
          kind: 'checkboxes' as const,
          options: question.options.map(o => o.label),
        },
      };
    }

    if (question.kind === 'multiple_choice') {
      return {
        ...basePrompt,
        config: {
          kind: 'multiple_choice' as const,
          options: question.options.map(o => o.label),
        },
      };
    }

    if (question.kind === 'address') {
      return {
        ...basePrompt,
        config: {
          kind: 'address' as const,
          allowPoBox: question.allowPoBox,
          supportedCountries: stripSupportedCountries(question.supportedCountries),
        },
      };
    }

    throw new Error('Unknown question kind');
  }),
});

export default convertToApiPayload;
